// Hook para manejar notificaciones en tiempo real
// Usa Supabase Realtime para escuchar nuevas notificaciones

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export interface Notificacion {
  id: string;
  usuario_id: string;
  gasto_id: string | null;
  tipo: 'aprobado' | 'rechazado';
  mensaje: string;
  leida: boolean;
  created_at: string;
}

interface UseNotificacionesReturn {
  notificaciones: Notificacion[];
  loading: boolean;
  error: string | null;
  notificacionesNoLeidas: number;
  marcarComoLeida: (notificacionId: string) => Promise<void>;
  marcarTodasComoLeidas: () => Promise<void>;
  recargar: () => Promise<void>;
}

export function useNotificaciones(): UseNotificacionesReturn {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar notificaciones iniciales
  const cargarNotificaciones = useCallback(async () => {
    if (!user || user.rol !== 'Usuario') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('notificaciones')
        .select('*')
        .eq('usuario_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (queryError) {
        throw queryError;
      }

      setNotificaciones(data || []);
    } catch (err: any) {
      console.error('❌ Error al cargar notificaciones:', err);
      setError(err.message || 'Error al cargar notificaciones');
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    cargarNotificaciones();
  }, [cargarNotificaciones]);

  // Escuchar nuevas notificaciones en tiempo real
  useEffect(() => {
    if (!user || user.rol !== 'Usuario') {
      return;
    }

    console.log('🔔 [useNotificaciones] Suscribiéndose a notificaciones en tiempo real para usuario:', user.id);

    // ✅ MEJORADO: Usar un nombre de canal único y limpiar antes de crear uno nuevo
    const channelName = `notificaciones-usuario-${user.id}`;
    
    // Limpiar canales existentes con el mismo nombre
    const existingChannels = supabase.getChannels();
    existingChannels.forEach((ch) => {
      if (ch.topic === channelName) {
        console.log('🔔 [useNotificaciones] Limpiando canal existente:', channelName);
        supabase.removeChannel(ch);
      }
    });

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificaciones',
          filter: `usuario_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('🔔 [useNotificaciones] Nueva notificación recibida:', payload.new);
          const nuevaNotificacion = payload.new as Notificacion;
          
          // Agregar nueva notificación al inicio de la lista
          setNotificaciones((prev) => {
            // Evitar duplicados
            if (prev.some((n) => n.id === nuevaNotificacion.id)) {
              return prev;
            }
            return [nuevaNotificacion, ...prev];
          });

          // Mostrar notificación del navegador (si está permitido)
          if ('Notification' in window) {
            if (Notification.permission === 'granted') {
              new Notification('Caja Chica', {
                body: nuevaNotificacion.mensaje,
                icon: '/favicon.ico',
                tag: nuevaNotificacion.id, // Evitar duplicados
              });
            } else if (Notification.permission === 'default') {
              // Solicitar permiso la primera vez
              Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                  new Notification('Caja Chica', {
                    body: nuevaNotificacion.mensaje,
                    icon: '/favicon.ico',
                    tag: nuevaNotificacion.id,
                  });
                }
              });
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('🔔 [useNotificaciones] Estado de suscripción:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ [useNotificaciones] Suscrito a notificaciones en tiempo real');
          // Limpiar error si se suscribió correctamente
          setError(null);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ [useNotificaciones] Error en canal de notificaciones');
          // ✅ MEJORADO: No mostrar error permanente, solo loguear
          // El sistema intentará reconectarse automáticamente
          console.warn('⚠️ [useNotificaciones] Reintentando conexión...');
        } else if (status === 'TIMED_OUT') {
          console.warn('⚠️ [useNotificaciones] Timeout en suscripción a notificaciones');
          // ✅ MEJORADO: No mostrar error permanente
          console.warn('⚠️ [useNotificaciones] Reintentando conexión...');
        }
      });

    return () => {
      console.log('🔔 [useNotificaciones] Desuscribiéndose de notificaciones');
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Marcar notificación como leída
  const marcarComoLeida = useCallback(
    async (notificacionId: string) => {
      if (!user) return;

      try {
        const { error: updateError } = await supabase
          .from('notificaciones')
          .update({ leida: true })
          .eq('id', notificacionId)
          .eq('usuario_id', user.id);

        if (updateError) {
          throw updateError;
        }

        // Actualizar estado local
        setNotificaciones((prev) =>
          prev.map((n) => (n.id === notificacionId ? { ...n, leida: true } : n))
        );
      } catch (err: any) {
        console.error('❌ Error al marcar notificación como leída:', err);
        // No mostrar error al usuario, solo loguear
      }
    },
    [user]
  );

  // Marcar todas las notificaciones como leídas
  const marcarTodasComoLeidas = useCallback(async () => {
    if (!user) return;

    try {
      const { error: updateError } = await supabase
        .from('notificaciones')
        .update({ leida: true })
        .eq('usuario_id', user.id)
        .eq('leida', false);

      if (updateError) {
        throw updateError;
      }

      // Actualizar estado local
      setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
    } catch (err: any) {
      console.error('❌ Error al marcar todas las notificaciones como leídas:', err);
    }
  }, [user]);

  // Recargar notificaciones manualmente
  const recargar = useCallback(async () => {
    await cargarNotificaciones();
  }, [cargarNotificaciones]);

  const notificacionesNoLeidas = notificaciones.filter((n) => !n.leida).length;

  return {
    notificaciones,
    loading,
    error,
    notificacionesNoLeidas,
    marcarComoLeida,
    marcarTodasComoLeidas,
    recargar,
  };
}

